import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { BillingSubscribeResponse } from '../billing/billing-payment.types';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import { useAppQuery } from '../use-app-query.hook';

type FetchCompany = {
  companyId: string;
  enabled?: boolean;
  onError?: (error: HttpException | unknown) => void;
};

// Offer checkouts: the admin can edit the offer's plan/prices while the customer has this page
// open, and this response is what the summary displays — refresh it on focus so the shown price
// matches what checkout will charge. Regular checkouts keep focus refetch off (the subscribe
// endpoint provisions Stripe state; re-hitting it on every focus is unsafe).
export const shouldRefetchSubscribeOnFocus = (data?: BillingSubscribeResponse): boolean =>
  Boolean(data?.offer);

export const useClientSecret = ({ companyId, enabled = true, onError }: FetchCompany) => {
  const queryClient = useQueryClient();

  const { data, isLoading, refetch } = useAppQuery<BillingSubscribeResponse>({
    queryKey: [QueryKey.GET_CLIENT_SECRET, companyId],
    queryFn: () =>
      ApiService.get({ endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'subscribe'] }),
    enabled: !!companyId && enabled,
    retry: false,
    onError,
    refetchOnWindowFocus: (query) => shouldRefetchSubscribeOnFocus(query.state.data),
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_CLIENT_SECRET] });
  }, [queryClient]);

  return {
    isLoading,
    invalidateQuery,
    refetch,
    ...data,
  };
};
